import { FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@material-ui/core';
import { CpfCnpjMask } from 'components/masks';
import { Button, MdSpace, TextField } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { updateInformacoesPrincipaisField } from 'store/modules/investidores/registro';
import { validaCpfCnpj } from 'utils/validation';
import { TipoInvestidor } from 'common/genericTypes';

interface ValidacaoCpfCnpjProps {
  onSubmit: () => void;
  onSubmitFundoInvestidor?: (cnpj: string) => void;
}

const mapStateToProps = (state: RootState) => ({
  investidorApi: state.investidorApi,
});

const mapDispatchToProps = { updateInformacoesPrincipaisField };

type Props = typeof mapDispatchToProps & ValidacaoCpfCnpjProps & ReturnType<typeof mapStateToProps>;

const ValidacaoCpfCnpj: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    updateInformacoesPrincipaisField,
    investidorApi: { getInvestidores },
  } = props;

  const [cpfCnpjInvestidor, setCpfCnpjInvestidor] = useState<string>();
  const [isCpfCnpjUnique, setIsCpfCnpjUnique] = useState<boolean>(true);
  const [isCpfCnpjValid, setIsCpfCnpjValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [tipoInvestidor, setTipoInvestidor] = useState<'INVESTIDOR' | 'FUNDO'>('INVESTIDOR');

  const handleCnpjChange = (value: string) => {
    value = value.replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
    if (value.length > 14) {
      return;
    }
    setError(false);
    setIsCpfCnpjUnique(true);
    setIsCpfCnpjValid(false);
    const isNumericOnly = /^\d+$/.test(value);
    const validLength = isNumericOnly ? [11, 14].includes(value.length) : value.length === 14;
    if (validLength) {
      setLoading(true);
      setIsCpfCnpjValid(validaCpfCnpj(value));
      getInvestidores(dispatch, { cpfCnpj: value }, true)
        .then((investidores) => {
          setIsCpfCnpjUnique(!investidores?.length);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
    setCpfCnpjInvestidor(value);
  };

  const isAlphanumericCnpj = (value: string) => /[a-zA-Z]/.test(value);

  const handleSubmit = () => {
    if (cpfCnpjInvestidor) {
      if (tipoInvestidor === 'FUNDO') {
        if (props.onSubmitFundoInvestidor) {
          props.onSubmitFundoInvestidor(cpfCnpjInvestidor);
        }
      } else {
        updateInformacoesPrincipaisField({ name: 'cpfCnpj', value: cpfCnpjInvestidor });
        const isPj = isAlphanumericCnpj(cpfCnpjInvestidor) || cpfCnpjInvestidor.length > 11;
        updateInformacoesPrincipaisField({ name: 'isPj', value: isPj });
        props.onSubmit();
      }
    }
  };

  useEffect(() => {
    const isNumericOnly = /^\d+$/.test(cpfCnpjInvestidor ?? '');
    const validLength = isNumericOnly
      ? [11, 14].includes(cpfCnpjInvestidor?.length ?? 0)
      : (cpfCnpjInvestidor?.length ?? 0) === 14;
    setError(validLength && (!isCpfCnpjUnique || !isCpfCnpjValid));
  }, [cpfCnpjInvestidor, isCpfCnpjUnique, isCpfCnpjValid]);

  const isInputValid = () => {
    if (!cpfCnpjInvestidor || loading || error) {
      return false;
    }
    const isNumericOnly = /^\d+$/.test(cpfCnpjInvestidor);
    const validLength = isNumericOnly
      ? [11, 14].includes(cpfCnpjInvestidor.length)
      : cpfCnpjInvestidor.length === 14;
    if (!validLength || !isCpfCnpjUnique || !isCpfCnpjValid) {
      return false;
    }
    
    if (tipoInvestidor === 'FUNDO' && isNumericOnly && cpfCnpjInvestidor.length !== 14) {
      return false;
    }
    
    return true;
  };

  return (
    <>
      <Grid container justify="center">
        <Grid item sm={4}>
          <Typography variant="h3" align="center">
            {'Antes de começar, nos informe o CPF OU CNPJ a ser cadastrado:'}
          </Typography>
          <MdSpace />
          <TextField
            label="CNPJ/CPF do Investidor"
            value={CpfCnpjMask(cpfCnpjInvestidor ?? '')}
            onChange={handleCnpjChange}
            error={error}
            errorMessage={
              (!isCpfCnpjValid && 'CNPJ/CPF inválido') || 
              (!isCpfCnpjUnique && 'CNPJ/CPF já cadastrado') || 
              (tipoInvestidor === 'FUNDO' && cpfCnpjInvestidor?.length === 11 && 'Fundos de investimento devem ter CNPJ') || 
              undefined
            }
          />
          <MdSpace />
          
          <Typography variant="subtitle1">Tipo de Investidor:</Typography>
          <RadioGroup 
            row 
            value={tipoInvestidor} 
            onChange={(e) => setTipoInvestidor(e.target.value as 'INVESTIDOR' | 'FUNDO')}
          >
            <FormControlLabel value="INVESTIDOR" control={<Radio color="primary" />} label="Investidor" data-testId="tipoInvestidorInvestidor" />
            <FormControlLabel value="FUNDO" control={<Radio color="primary" />} label="Fundo Investidor" data-testId="tipoInvestidorFundo" />
          </RadioGroup>
          <MdSpace />
        </Grid>
      </Grid>
      <Grid container justify="center">
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isInputValid()}
          >
            {'Prosseguir'}
          </Button>
          <MdSpace />
        </Grid>
      </Grid>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ValidacaoCpfCnpj);
